/**
 * KST(UTC+9, 한국은 DST 없음) 날짜 헬퍼. 서버·클라 공용(순수 함수). (JCG-2310)
 *
 * 한국은 DST가 없어 고정 오프셋(+9h)으로 충분하다. Date 객체 대신 ISO 날짜
 * 문자열(YYYY-MM-DD)을 다뤄 타임존 드리프트를 피한다. KST offset 로직은
 * attendanceRecordActions·finance 등 여러 곳에서 중복 선언되던 것을 여기로 통합했다(JCG-2486).
 *
 * 이 모듈은 **연산만** 둔다. 사용자에게 보이는 표기(Intl.DateTimeFormat)는 `kst-format.ts` 에 있다.
 * pass-sales → payment-price-validation 을 타는 결제 서버 경로 전체가 이 모듈을 import 하므로,
 * 여기에 포맷터를 두면 포맷하지 않는 경로까지 cold start 마다 ko-KR ICU 데이터를 로드한다(JCG-3064).
 */
export const KST_OFFSET_MS = 9 * 60 * 60 * 1000;

const BARE_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/**
 * 시각 문자열을 Date 로 파싱한다. bare date(YYYY-MM-DD)는 **KST 자정**으로 해석한다.
 *
 * `new Date("2026-10-25")` 는 UTC 자정(= KST 09:00)이라, 시험일·판매 경계처럼 KST 달력 날짜를
 * 시각으로 쓰는 곳은 `+09:00` 접미사를 붙여야 한다. 그 규칙을 이 한 곳에 둔다.
 * 오프셋이 있는 ISO 시각은 그대로 파싱한다.
 */
export function parseKstInstant(iso: string): Date {
  return new Date(BARE_DATE_PATTERN.test(iso) ? `${iso}T00:00:00+09:00` : iso);
}

/** epoch ms를 (이미 KST offset이 반영된 ms라는 전제 하에) YYYY-MM-DD로 변환 */
export function msToKstDateStr(ms: number): string {
  return new Date(ms).toISOString().slice(0, 10);
}

/** TIMESTAMPTZ ISO 문자열(또는 Date 파싱 가능한 문자열)을 KST 날짜(YYYY-MM-DD)로 변환 */
export function toKstDateStr(iso: string): string {
  return msToKstDateStr(new Date(iso).getTime() + KST_OFFSET_MS);
}

/** KST "지금"을 +9h 시프트한 Date (UTC getter로 KST 값 읽기) */
export function nowKstShifted(): Date {
  return new Date(Date.now() + KST_OFFSET_MS);
}

/** KST 기준 오늘 날짜(YYYY-MM-DD) */
export function todayKstISO(): string {
  return msToKstDateStr(Date.now() + KST_OFFSET_MS);
}
